<script setup>
import TabContainer from "@/components/global-themed/tabs/TabContainer.vue";
import TabItem from "@/components/global-themed/tabs/TabItem.vue";
import useEmailSettings from "@/composables/email-settings/useEmailSettings";
import {computed, ref} from "vue";
import ConfirmationModal from "@/components/settings/notifications/modals/ConfirmationModal.vue";
import {kitName} from "@/composables/useLocalDomains";
import WeeklyDayPicker from "@/components/settings/notifications/WeeklyDayPicker.vue";
import AccordionTransition from "@/components/transitions/AccordionTransition.vue";

const {settingsQuery, saveEmailFrequencyMutation, weeklyDaySetting} = useEmailSettings();
const {mutate: saveFrequency, isPending} = saveEmailFrequencyMutation;
const emailFrequencySetting = computed(() => settingsQuery.data.value?.email_frequency);

const pendingSelection = ref(null);

const getDefaultDay = () => {
  const today = new Date().getDay() // 0 = Sun, 6 = Sat

  // fall back to Monday as we don't send emails on weekends
  if (today === 0 || today === 6) {
    return 1;
  }

  return today;
}

const dayOfWeekSelection = computed({
  get: () => weeklyDaySetting.value ?? getDefaultDay(),
  set: (value) => {
    if (isPending.value) {
      return;
    }

    submitChange('weekly', value);
  },
})

const confirmationFrequencies = new Set(["off", "weekly", 'daily']);
const needsConfirmation = (frequency) => confirmationFrequencies.has(frequency);

const confirmationCopy = {
  off: {
    heading: "Turn Off Email Notifications?",
    text: "Turning this off means you won't receive any emails for any websites in this account. Updates will still be created and available in the platform, but you won't be notified when they're ready for approval. You can still enable emails for individual websites if needed.",
    subtext: "Are you sure you want to turn off email notifications for this account?",
    confirmLabel: "Turn off emails",
    cancelLabel: "Cancel",
    state: "sad",
  },
  daily: {
    heading: "Switch to Daily Email Summary?",
    text: "Instead of receiving emails in real time, all updates across your websites will be grouped into a single daily email. Updates will be organised by website so you can easily review what needs approval. You can still override this setting for individual websites.",
    subtext: "Are you sure you want to switch to daily emails?",
    confirmLabel: "Enable daily emails",
    cancelLabel: "Cancel",
    state: "default",
  },
  weekly: {
    heading: "Switch to Weekly Email Summary?",
    text: "All updates across your websites will be grouped into a single weekly email, sent on your selected day. Updates will be organised by website so you can review everything in one place. You can still override this setting for individual websites.",
    subtext: "Are you sure you want to switch to weekly emails?",
    confirmLabel: "Enable weekly emails",
    cancelLabel: "Cancel",
    state: "default",
  },
};

const handleFrequencySelection = (frequency) => {
  if (frequency === emailFrequencySetting.value) {
    return;
  }

  if (needsConfirmation(frequency)) {
    pendingSelection.value = frequency;
    return;
  }

  if (isPending.value) {
    return;
  }

  submitChange(frequency)
}

const submitChange = (frequency, weeklyDay = null) => {
  if (isPending.value) {
    return;
  }

  const payload = {email_frequency: frequency};

  if (frequency === 'weekly') {
    payload.weekly_day = weeklyDay ?? dayOfWeekSelection.value;
  }

  saveFrequency(payload);
}

const modalCopy = computed(() => {
  if (!pendingSelection.value) {
    return null;
  }

  return confirmationCopy[pendingSelection.value];
});

const closeModal = () => {
  pendingSelection.value = null;
};

const confirmChange = () => {
  if (!pendingSelection.value) {
    return;
  }

  submitChange(pendingSelection.value);
  pendingSelection.value = null;
};

const frequencyOptions = [
  {label: "Real-time", value: "realtime"},
  {label: "Daily", value: "daily"},
  {label: "Weekly", value: "weekly"},
  {label: "Off", value: "off"},
];
</script>

<template>
  <div class="tw-bg-white tw-rounded-lg tw-p-8 tw-flex tw-flex-col tw-gap-8">
    <div class="tw-flex tw-flex-col tw-gap-6">
      <div class="tw-flex tw-justify-between tw-items-center">
        <p class="tw-font-bold tw-text-neutral-900 tw-text-2.5xl">
          Email Frequency
        </p>

        <TabContainer class="tw-grid tw-grid-cols-4 tw-gap-2">
          <TabItem
            v-for="option in frequencyOptions"
            :key="option.value"
            :active="emailFrequencySetting === option.value"
            :class="[
              'tw-justify-center',
              {
                'tw-pointer-events-none': isPending,
              }
            ]"
            class="tw-justify-center"
            @click="handleFrequencySelection(option.value)"
          >
            <span>{{ option.label }}</span>
          </TabItem>
        </TabContainer>
      </div>

      <p class="tw-text-neutral-900 tw-text-base tw-leading-snug">
        Control how often your agency receives email notifications when {{ kitName }} completes work that requires approval. Choose to receive notifications in real-time, as a daily or weekly summary, or turn them off entirely. Summary emails group all items that need review across your websites, helping teams managing multiple sites reduce email volume while still staying on top of approvals.
      </p>
    </div>

    <AccordionTransition>
      <div
        v-if="emailFrequencySetting === 'weekly'"
        class="tw-transition-all tw-duration-300 tw-ease-in-out"
      >
        <WeeklyDayPicker
          v-model="dayOfWeekSelection"
          :disabled="isPending"
        />
      </div>
    </AccordionTransition>
  </div>

  <ConfirmationModal
    v-if="pendingSelection && modalCopy"
    :state="modalCopy.state"
    :confirm-label="modalCopy.confirmLabel"
    :cancel-label="modalCopy.cancelLabel"
    @close="closeModal"
    @confirm="confirmChange"
  >
    <template #heading>
      {{ modalCopy.heading }}
    </template>
    <template #text>
      {{ modalCopy.text }}
    </template>
    <template #subtext>
      {{ modalCopy.subtext }}
    </template>
  </ConfirmationModal>
</template>
